import React, { useEffect, useState } from "react";
import { X, IndianRupee } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";

interface AddAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, remark: string) => void;
  customerName: string;
  totalBillAmount: number;
  paymentApproved: number;
  remainingAmount: number;
  isLoading: boolean | undefined;
}

const AddAdjustmentModal: React.FC<AddAdjustmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  customerName,
  totalBillAmount,
  paymentApproved,
  remainingAmount,
  isLoading,
}) => {
  const [adjustmentAmount, setAdjustmentAmount] =
    useState<number>(remainingAmount);
  const [adjustmentRemark, setAdjustmentRemark] = useState<string>("");

  const handleWheelPrevent = usePreventWheelChange();

  useEffect(() => {
    setAdjustmentAmount(remainingAmount);
    setAdjustmentRemark("");
  }, [remainingAmount, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Adjust Payment Amount
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="font-medium text-gray-900">{customerName}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Bill Amount:</span>
              <span className="font-semibold text-gray-900">
                ₹{totalBillAmount.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Approved Amount:</span>
              <span className="font-semibold text-green-600">
                ₹{paymentApproved.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                Remaining Amount:
              </span>
              <span className="font-semibold text-red-600">
                ₹{remainingAmount.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          <div className="">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adjustment Amount
            </label>
            <div className="relative mb-2">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                min={0}
                max={remainingAmount}
                value={adjustmentAmount}
                onWheel={handleWheelPrevent}
                onChange={(e) => setAdjustmentAmount(Number(e.target.value))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter adjustment amount"
              />
            </div>
            <p className="text-xs text-gray-500">
              Maximum adjustable amount: ₹
              {remainingAmount.toLocaleString("en-IN")}
            </p>
            <p className="text-xs text-gray-500">
              Adjusted against total payment from dealer commission
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adjustment Remark (optional)
            </label>
            <textarea
              value={adjustmentRemark}
              onChange={(e) => setAdjustmentRemark(e.target.value)}
              rows={3}
              placeholder="Enter reason / notes for this adjustment"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              This remark will be saved with the adjustment.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type={ButtonType.Cancel}
              onClick={onClose}
              className="text-sm"
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Extra}
              onClick={() => onConfirm(adjustmentAmount, adjustmentRemark)}
              disabled={
                isLoading ||
                adjustmentAmount <= 0 ||
                adjustmentAmount > remainingAmount
              }
              className="text-sm"
            >
              {isLoading ? "Processing..." : "Confirm Adjustment"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAdjustmentModal;
